/* ReportsPanel — Admin tab listing open community moderation reports.
 * Backend: GET /api/admin/reports?status=open, POST /api/admin/reports/{id}/resolve,
 *          POST /api/admin/reports/{id}/dismiss.
 *
 * Each report shows: who reported it, what they said, what was reported (with a
 * link to view the target), and admin actions:
 *   - Dismiss (false positive)
 *   - Resolve (no delete)
 *   - Resolve + DELETE (one-click "review and nuke")
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle, X, Trash, ArrowSquareOut, Flag } from "@phosphor-icons/react";
import { recipePath, cookbookPath } from "@/lib/slug";

const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
];

export default function ReportsPanel() {
  const [status, setStatus] = useState("open");
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports?status=${status}`);
      setReports(data);
    } catch {
      toast.error("Couldn't load reports");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load() is a stable closure; re-fetch only when the status filter changes
  useEffect(() => { load(); }, [status]);

  const dismiss = async (rep) => {
    if (!window.confirm("Dismiss this report? Marks it resolved with no action taken.")) return;
    setBusy(rep.report_id);
    try {
      await api.post(`/admin/reports/${rep.report_id}/dismiss`);
      toast.success("Dismissed");
      load();
    } catch {
      toast.error("Dismiss failed");
    } finally {
      setBusy(null);
    }
  };

  const resolve = async (rep, deleteTarget) => {
    const action_taken = window.prompt(deleteTarget ? `Resolve and DELETE this ${rep.target_kind}? Optional admin note:` : "Resolve without deleting? Optional admin note:");
    if (action_taken === null) return;
    setBusy(rep.report_id);
    try {
      await api.post(`/admin/reports/${rep.report_id}/resolve`, {
        action_taken,
        delete_target: !!deleteTarget,
      });
      toast.success(deleteTarget ? "Resolved + target deleted" : "Resolved");
      load();
    } catch {
      toast.error("Resolve failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-reports-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="overline text-rf-orange mb-1">Community moderation</p>
          <h2 className="font-display text-2xl font-black text-rf-ink">Reports queue</h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="reports-status-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`reports-tab-${t.key}`}
              className={`text-xs px-3 py-1.5 rounded-full font-bold transition-colors ${status === t.key ? "bg-rf-ink text-rf-cream" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-rf-ink-soft text-sm">Loading…</p>}
      {!loading && reports.length === 0 && (
        <div className="card-recipe text-center py-12" data-testid="reports-empty">
          <Flag size={40} weight="duotone" className="mx-auto text-rf-orange/40 mb-3" />
          <p className="text-rf-ink-soft">
            {status === "open" ? "No open reports — community moderation queue is empty 🎉" : `No ${status} reports.`}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {reports.map((rep) => {
          const snap = rep.target_snapshot || {};
          const targetTitle = snap.title || `(${rep.target_kind} ${rep.target_id})`;
          const targetGone = snap.deleted;
          const isResolved = rep.status === "resolved";
          const isDismissed = rep.status === "dismissed";
          return (
            <div key={rep.report_id} data-testid={`report-${rep.report_id}`} className={`card-recipe ${isResolved ? "opacity-60" : ""} ${isDismissed ? "opacity-40" : ""}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-[0.18em] text-rf-orange">{rep.target_kind}</span>
                    {targetGone && <span className="chip !bg-red-100 !text-red-800 !text-[10px]">target deleted</span>}
                    {isResolved && <span className="chip !bg-emerald-100 !text-emerald-800 !text-[10px]">resolved</span>}
                    {isDismissed && <span className="chip !bg-rf-cream-alt !text-rf-ink-soft !text-[10px]">dismissed</span>}
                  </div>
                  <h3 className="font-display font-extrabold text-rf-ink text-lg truncate" title={targetTitle}>{targetTitle}</h3>
                  <p className="text-xs text-rf-ink-soft mt-0.5">
                    Reported by <span className="font-mono">{rep.reporter_email || rep.reporter_id}</span>
                    {" · "}
                    {new Date(rep.created_at).toLocaleString()}
                  </p>
                </div>
                {!targetGone && (
                  <a
                    href={rep.target_kind === "recipe"
                      ? recipePath(rep.target_id, snap.title)
                      : cookbookPath(rep.target_id, snap.title)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline-ink !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
                    data-testid={`report-view-${rep.report_id}`}
                  >
                    View <ArrowSquareOut size={12} weight="bold" />
                  </a>
                )}
              </div>

              <div className="bg-rf-cream-alt rounded-lg p-3 text-sm text-rf-ink mt-2 mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-rf-ink-soft mb-1">Reason</p>
                <p className="whitespace-pre-wrap">{rep.reason}</p>
              </div>

              {rep.action_taken && (
                <div className="bg-rf-amber/10 rounded-lg p-3 text-sm mt-2 mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-rf-orange mb-1">Admin note</p>
                  <p className="whitespace-pre-wrap text-rf-ink">{rep.action_taken}</p>
                </div>
              )}

              {rep.status === "open" && !targetGone && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => dismiss(rep)}
                    disabled={busy === rep.report_id}
                    data-testid={`report-dismiss-${rep.report_id}`}
                    className="text-xs px-3 py-1.5 rounded-lg bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-ink/10 inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <X size={12} weight="bold" /> Dismiss
                  </button>
                  <button
                    onClick={() => resolve(rep, false)}
                    disabled={busy === rep.report_id}
                    data-testid={`report-resolve-${rep.report_id}`}
                    className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-800 hover:bg-emerald-500/30 inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <CheckCircle size={12} weight="bold" /> Resolve (no delete)
                  </button>
                  <button
                    onClick={() => resolve(rep, true)}
                    disabled={busy === rep.report_id}
                    data-testid={`report-resolve-delete-${rep.report_id}`}
                    className="text-xs px-3 py-1.5 rounded-lg bg-red-600/90 text-white hover:bg-red-600 inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <Trash size={12} weight="bold" /> Resolve + DELETE
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
